import { trackGeometry } from '../../lib/gpx'
import { routeGeometry } from '../../lib/route'
import type { DiaryEntry, Stage } from '../../lib/types'

/**
 * Die beiden Distanzen der sichtbaren Linien und ihre Abweichung.
 *
 * Die beiden Quellwerte stehen hier, wie sie im Dokument stehen. Angezeigt wird
 * keiner von ihnen unmittelbar: dafür gibt es kilometerGenau, und dort steht
 * auch, warum eine Stelle hinter dem Komma alles ist, was sie hergeben.
 */
export interface EtappenVergleich {
  geplantKm: number
  gefahrenKm: number
  /** Positiv bedeutet einen Umweg: gefahren minus geplant, auf 0.1 km gerundet. */
  differenzKm: number
}

/**
 * Vergleicht eine gültige berechnete Strecke mit einer gültigen Aufzeichnung.
 *
 * Eine Luftlinie hat keine belastbare Distanz und eine veraltete Route gehört
 * zu anderen Endpunkten. In beiden Fällen gibt es deshalb keine Zahl, die
 * Genauigkeit nur vortäuschen würde.
 */
export function etappenVergleich(stage: Stage, entry?: DiaryEntry): EtappenVergleich | undefined {
  if (!routeGeometry(stage) || !trackGeometry(entry) || !stage.route || !entry?.track) {
    return undefined
  }

  const geplantKm = stage.route.distanceKm
  const gefahrenKm = entry.track.distanceKm
  if (!Number.isFinite(geplantKm) || !Number.isFinite(gefahrenKm)) return undefined

  return { geplantKm, gefahrenKm, differenzKm: runde(gefahrenKm - geplantKm) }
}

/**
 * Auf eine Nachkommastelle - so genau, wie die gröbere der beiden Quellen ist.
 *
 * Die Aufzeichnung wird auf den Meter genau aufsummiert, die geplante Strecke
 * dagegen kommt schon gerundet aus dem Backend (roundTo(..., 1) in
 * internal/routing/routing.go). Damit steht hinter jeder geplanten Zahl eine
 * Unsicherheit von bis zu 0.05 km, und eine Differenz wie "+2.44 km" hätte
 * ihre zweite Stelle erfunden - abgelesen an einer Zahl, die es so genau nie
 * gab. Eine Stelle weniger ist keine Ungenauigkeit, sondern die vorhandene.
 *
 * Dieselbe Stufung für alle drei Zahlen, und zwar aus einem zweiten Grund:
 * gefahren, geplant und Differenz stehen nebeneinander, und wer sie im Kopf
 * nachrechnet, soll auf das Ergebnis kommen, das danebensteht. Weil die
 * geplante Zahl ohnehin auf dem Zehntelraster liegt, geht die Rechnung auf.
 */
function runde(wert: number): number {
  return Math.round(wert * 10) / 10
}

const kilometerFormat = new Intl.NumberFormat('de-CH', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
})

/** Anzeige mit einer Nachkommastelle, siehe runde(). */
export function kilometerGenau(wert: number): string {
  return kilometerFormat.format(runde(wert))
}

/** Abweichung mit Vorzeichen; null bleibt als ±0 sichtbar. */
export function differenzGenau(wert: number): string {
  const gerundet = runde(wert)
  if (gerundet === 0) return '±0'
  return `${gerundet > 0 ? '+' : '−'}${kilometerGenau(Math.abs(gerundet))}`
}
